import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, MapPin, Calendar as CalendarIcon, Users } from "lucide-react";
import { useLang } from "@/lib/i18n";

/**
 * Traveller-facing search bar shown in the Landing hero.
 * Submits to /strutture with query params so Directory pre-filters + Vetrina
 * booking dialog pre-fills dates.
 */
export default function HeroSearchBar() {
  const { lang } = useLang();
  const IT = lang === "it";
  const nav = useNavigate();
  const today = new Date().toISOString().slice(0, 10);
  const [city, setCity] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [guests, setGuests] = useState("2");

  const submit = (e) => {
    e?.preventDefault();
    const p = new URLSearchParams();
    if (city.trim()) p.set("q", city.trim());
    if (checkIn) p.set("check_in", checkIn);
    if (checkOut) p.set("check_out", checkOut);
    if (guests) p.set("guests", guests);
    nav(`/strutture${p.toString() ? "?" + p.toString() : ""}`);
  };

  return (
    <form onSubmit={submit}
      className="mt-8 border border-border bg-card/95 backdrop-blur-sm rounded-xl p-3 shadow-lg grid grid-cols-1 sm:grid-cols-[1fr_1fr_0.8fr_auto] gap-2 items-end"
      data-testid="hero-search-bar"
    >
      <div className="sm:col-span-4">
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-1"><MapPin className="w-3 h-3"/>{IT ? "Dove" : "Where"}</label>
        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder={IT ? "Città, zona o regione" : "City, area or region"} className="h-11" data-testid="hero-search-city"/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-1"><CalendarIcon className="w-3 h-3"/>Check-in</label>
        <Input
          type="date" min={today}
          value={checkIn}
          onChange={(e) => {
            const ci = e.target.value;
            setCheckIn(ci);
            if (checkOut && ci && checkOut <= ci) setCheckOut("");
          }}
          className="h-11" data-testid="hero-search-checkin"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-1"><CalendarIcon className="w-3 h-3"/>Check-out</label>
        <Input
          type="date" min={checkIn || today}
          value={checkOut}
          onChange={(e) => setCheckOut(e.target.value)}
          className="h-11" data-testid="hero-search-checkout"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-1"><Users className="w-3 h-3"/>{IT ? "Ospiti" : "Guests"}</label>
        <Input type="number" min={1} max={30} value={guests} onChange={(e) => setGuests(e.target.value)} className="h-11" data-testid="hero-search-guests"/>
      </div>
      <Button type="submit" size="lg" className="h-11 lift gap-1.5" data-testid="hero-search-submit">
        <Search className="w-4 h-4"/>{IT ? "Cerca" : "Search"}
      </Button>
    </form>
  );
}
